import { motion } from 'framer-motion'
import { Zap, ArrowRight, Calendar, Search, Mail, FileText, MessageSquare, Users } from 'lucide-react'
import { useState } from 'react'
import { capabilities } from '../../data/frameworks'

interface StepProps {
  onNext: () => void
}

const Step3 = ({ onNext }: StepProps) => {
  const [selectedCapability, setSelectedCapability] = useState<string | null>(null)

  const capabilityIcons = {
    'task-management': Calendar,
    'research': Search,
    'communication': Mail,
    'document-analysis': FileText,
    'scheduling': Calendar,
    'collaboration': Users
  }

  const examples = {
    'task-management': {
      prompt: "Organize my week based on upcoming deadlines",
      response: "I've analyzed your calendar and tasks. Here's your optimized schedule:\n\n Monday: Focus on the quarterly report (high priority)\n Tuesday: Client meetings + project review\n Wednesday: Deep work on product specs\n Thursday: Team sync + code reviews\n Friday: Documentation and planning"
    },
    'research': {
      prompt: "Research the latest trends in sustainable packaging",
      response: "I've compiled research from 50+ sources. Key findings:\n\n1. Biodegradable materials growing 23% YoY\n2. Consumer preference shifting to minimal packaging\n3. Top innovations: mushroom-based, seaweed films\n4. Regulatory changes expected in EU by 2025\n\nShall I create a detailed report?"
    },
    'communication': {
      prompt: "Draft a follow-up email to the investor meeting",
      response: "Based on your meeting notes and style preferences:\n\nSubject: Next Steps - Investment Discussion\n\nHi Sarah,\n\nGreat connecting yesterday. As discussed, I've attached our updated projections.\n\nKey points:\n- Q4 growth trajectory\n- Expansion timeline\n- Team scaling plans\n\nHappy to schedule a follow-up call.\n\nBest,\n[Your name]"
    },
    'document-analysis': {
      prompt: "Summarize this 50-page contract",
      response: "Contract Summary:\n\n Terms: 2-year agreement, auto-renewal\n Key Obligations: Monthly deliverables, quarterly reviews\n Payment: Net-30, with 2% early payment discount\n Termination: 90-day notice required\n\n3 Potential Concerns Identified:\n1. Non-compete clause (Section 7.2)\n2. IP assignment terms (Section 12)\n3. Liability cap below industry standard"
    },
    'scheduling': {
      prompt: "Find time for a team offsite next month",
      response: "I've analyzed team calendars and preferences:\n\nOptimal Dates:\n1. March 15-16 (92% availability)\n2. March 22-23 (87% availability)\n\nRecommendation: March 15-16\n- No major deadlines that week\n- Weather forecast favorable\n- Venue availability confirmed\n\nShall I send calendar invites?"
    },
    'collaboration': {
      prompt: "Prepare the standup summary for the team",
      response: "Team Standup Summary - Today:\n\nProgress:\n Alice: Completed API integration\n Bob: 80% done with UI redesign\n Carol: Fixed 3 critical bugs\n\nBlockers:\n- Waiting on design assets (Bob)\n- API rate limits (Alice)\n\nAction Items:\n1. Design review at 2pm\n2. Deploy to staging by EOD\n\nSent to #engineering channel"
    }
  }

  return (
    <div className="min-h-[60vh] flex flex-col items-center justify-center px-4">
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ type: 'spring', duration: 0.6 }}
        className="w-20 h-20 rounded-2xl bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center mb-6 shadow-xl shadow-primary-500/30"
      >
        <Zap className="w-10 h-10 text-white" />
      </motion.div>

      <motion.h2
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="text-3xl md:text-4xl font-bold text-center mb-4"
      >
        Powerful <span className="gradient-text">Capabilities</span>
      </motion.h2>

      <motion.p
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="text-lg text-surface-600 dark:text-surface-400 text-center max-w-2xl mb-12"
      >
        From managing tasks to conducting research and crafting communications,
        your AI Twin handles a wide range of tasks with intelligence and precision.
      </motion.p>

      {/* Capability Grid */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="grid grid-cols-2 md:grid-cols-3 gap-4 max-w-4xl mb-8"
      >
        {capabilities.map((cap, index) => {
          const Icon = capabilityIcons[cap.id as keyof typeof capabilityIcons] || Zap
          return (
            <motion.button
              key={cap.id}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.5 + index * 0.1 }}
              onClick={() => setSelectedCapability(selectedCapability === cap.id ? null : cap.id)}
              className={`card-hover p-4 text-left transition-all ${
                selectedCapability === cap.id
                  ? 'ring-2 ring-primary-500 border-primary-500'
                  : ''
              }`}
            >
              <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${cap.color} flex items-center justify-center mb-3`}>
                <Icon className="w-5 h-5 text-white" />
              </div>
              <h3 className="font-semibold text-surface-900 dark:text-surface-100 mb-1">
                {cap.title}
              </h3>
              <p className="text-sm text-surface-600 dark:text-surface-400">
                {cap.description}
              </p>
            </motion.button>
          )
        })}
      </motion.div>

      {/* Example Interaction */}
      {selectedCapability && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-3xl card p-6 mb-8"
        >
          <h4 className="text-sm font-medium text-surface-500 dark:text-surface-500 mb-4">
            Example Interaction
          </h4>
          <div className="space-y-4">
            <div className="chat-bubble-user">
              <p className="text-sm">
                {examples[selectedCapability as keyof typeof examples]?.prompt}
              </p>
            </div>
            <div className="chat-bubble-assistant">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center flex-shrink-0">
                  <MessageSquare className="w-4 h-4 text-white" />
                </div>
                <pre className="text-sm whitespace-pre-wrap font-sans">
                  {examples[selectedCapability as keyof typeof examples]?.response}
                </pre>
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {!selectedCapability && (
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
          className="text-surface-500 dark:text-surface-500 mb-8"
        >
          Click a capability to see an example
        </motion.p>
      )}

      <motion.button
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2 }}
        onClick={onNext}
        className="btn-primary text-lg group"
      >
        Learn About Privacy
        <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
      </motion.button>
    </div>
  )
}

export default Step3
